import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import { Wallet, X, Loader2, Check } from "lucide-react";
import { SupplierAutocomplete } from "./SupplierAutocomplete";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const labelCls = "block text-[10px] font-bold uppercase text-slate-500 mb-1";

/**
 * "Mark as Purchased" modal for an existing MR — Purchasing edits final prices
 * per item, optionally chooses supplier, and toggles "Pay now?" to also post
 * the payment + close the ledger side in the same shot. Wraps
 * POST /material-requests/:id/purchase with pay_now/paid_from_account_id.
 */
export function MarkPurchasedModal({ req, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [supplier, setSupplier] = useState(req.supplier_name || "");
  const [supplierId, setSupplierId] = useState(req.supplier_id || "");
  const [items, setItems] = useState(() =>
    (req.items || []).map((it) => ({
      ...it,
      quantity: Number(it.quantity || 1),
      estimated_price: Number(it.estimated_price || 0),
    })),
  );
  const [payNow, setPayNow] = useState(true);
  const [payment, setPayment] = useState({
    paid_from_account_id: "",
    payment_method: "credit_card",
  });
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/finance/accounts")
      .then((rows) => {
        if (!alive) return;
        const active = (rows || []).filter((a) => a.active !== false);
        setAccounts(active);
        if (active.length > 0) setPayment((p) => ({ ...p, paid_from_account_id: active[0].id }));
      })
      .catch(() => setAccounts([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const total = useMemo(
    () => items.reduce((a, it) => a + Number(it.quantity || 0) * Number(it.estimated_price || 0), 0),
    [items],
  );

  const patchItem = (idx, patch) => {
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const submit = async () => {
    if (!supplier.trim()) { toast.error("Supplier is required"); return; }
    if (payNow && !payment.paid_from_account_id) { toast.error("Select an account or uncheck 'Pay now'"); return; }
    setBusy(true);
    try {
      await api.post(`/material-requests/${req.id}/purchase`, {
        actual_total: Number(total.toFixed(2)),
        supplier_id: supplierId || null,
        supplier_name: supplier.trim(),
        notes,
        items: items.map((it) => ({
          id: it.id,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit || "unit",
          estimated_price: Number(it.estimated_price),
          supplier_id: it.supplier_id,
          supplier_name: it.supplier_name || supplier.trim(),
          notes: it.notes,
          status: it.status,
        })),
        pay_now: payNow,
        paid_from_account_id: payNow ? payment.paid_from_account_id : null,
        payment_method: payNow ? payment.payment_method : null,
      });
      toast.success(payNow ? "Purchased & paid — posted to ledger" : "Marked as purchased");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to mark as purchased");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid={`mr-purchase-modal-${req.id}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><Wallet className="w-4 h-4" /> Mark as Purchased · {req.number}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Confirm the actual prices &amp; supplier. Toggle Pay now to also close the ledger side.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Supplier *</label>
            <SupplierAutocomplete
              value={supplier}
              supplierId={supplierId}
              onChange={({ name, id }) => { setSupplier(name); setSupplierId(id || ""); }}
              testId={`mrp-supplier-${req.id}`}
              placeholder="Type supplier name (creates new if not found)"
            />
          </div>

          {/* Items */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit price</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {items.map((it, idx) => {
              const lineTotal = Number(it.quantity || 0) * Number(it.estimated_price || 0);
              return (
                <div key={it.id || idx} className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-100 last:border-b-0">
                  <div className="col-span-6 text-xs text-slate-700 truncate">{it.description}</div>
                  <input type="text" inputMode="decimal" value={it.quantity}
                    data-testid={`mrp-qty-${it.id}`}
                    onChange={(e) => patchItem(idx, { quantity: e.target.value.replace(/[^0-9.]/g, "") })}
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" />
                  <input type="text" inputMode="decimal" value={it.estimated_price}
                    data-testid={`mrp-price-${it.id}`}
                    onChange={(e) => patchItem(idx, { estimated_price: e.target.value.replace(/[^0-9.]/g, "") })}
                    className="col-span-2 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" />
                  <div className="col-span-2 text-right text-xs font-semibold tabular-nums">{money(lineTotal)}</div>
                </div>
              );
            })}
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-slate-900 text-white text-sm">
              <div className="col-span-10 text-right font-bold uppercase text-xs">Total</div>
              <div className="col-span-2 text-right font-bold tabular-nums text-emerald-300">{money(total)}</div>
            </div>
          </div>

          {/* Pay now toggle */}
          <div className={`border rounded-lg p-3 ${payNow ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={payNow}
                data-testid={`mrp-pay-now-${req.id}`}
                onChange={(e) => setPayNow(e.target.checked)}
                className="w-4 h-4 rounded" />
              <div>
                <div className="text-xs font-bold text-slate-800 inline-flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> Pay now (post to ledger + deduct account)
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">Uncheck to mark as purchased but pay later (Pay button appears in the MR).</div>
              </div>
            </label>
            {payNow && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                <div>
                  <label className={labelCls}>Paid from account *</label>
                  {loading ? (
                    <div className="text-xs text-slate-400"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading...</div>
                  ) : accounts.length === 0 ? (
                    <div className="text-xs text-rose-600 border border-rose-200 rounded bg-rose-50 px-2 py-1">
                      No accounts. Go to Finance → Accounts first.
                    </div>
                  ) : (
                    <select
                      data-testid={`mrp-account-${req.id}`}
                      value={payment.paid_from_account_id}
                      onChange={(e) => setPayment({ ...payment, paid_from_account_id: e.target.value })}
                      className={inputCls}>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} · {a.type || "bank"} · {money(a.current_balance || 0)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Method</label>
                  <select value={payment.payment_method}
                    onChange={(e) => setPayment({ ...payment, payment_method: e.target.value })}
                    className={inputCls}>
                    <option value="credit_card">Credit card</option>
                    <option value="debit_card">Debit card</option>
                    <option value="bank">Bank transfer</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="wire">Wire</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Bought after 3 quotes, best price"
              className={inputCls} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl sticky bottom-0">
          <button onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-white">Cancel</button>
          <button
            data-testid={`mrp-confirm-${req.id}`}
            onClick={submit}
            disabled={busy || (payNow && accounts.length === 0)}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
          >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {payNow ? "Purchase & pay" : "Mark as purchased"}</button>
        </div>
      </div>
    </div>
  );
}

export default MarkPurchasedModal;
